(() => {
  if (window.iStoreFnbPageLoaded) return;
  window.iStoreFnbPageLoaded = true;

  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

  const setupRail = (rail) => {
    if (rail.dataset.iStoreFnbRailReady === "true") return;

    const viewport = rail.querySelector("[data-istore-fnb-viewport]");
    const track = rail.querySelector("[data-istore-fnb-track]");
    const items = [...rail.querySelectorAll("[data-istore-fnb-item]")];
    const previous = rail.querySelector("[data-istore-fnb-previous]");
    const next = rail.querySelector("[data-istore-fnb-next]");
    const dots = [...rail.querySelectorAll("[data-istore-fnb-dot]")];

    if (!viewport || !track || !items.length) return;

    rail.dataset.iStoreFnbRailReady = "true";

    const getStep = () => {
      const gap = Number.parseFloat(getComputedStyle(track).columnGap) || 0;
      return items[0].getBoundingClientRect().width + gap;
    };

    const getIndex = () => {
      const step = getStep();
      return step > 0
        ? Math.min(items.length - 1, Math.round(viewport.scrollLeft / step))
        : 0;
    };

    const update = () => {
      const maximum = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
      const index = getIndex();

      if (previous) previous.disabled = viewport.scrollLeft <= 1;
      if (next) next.disabled = viewport.scrollLeft >= maximum - 1;

      dots.forEach((dot, dotIndex) => {
        const active = dotIndex === index;
        if (active) {
          dot.setAttribute("aria-current", "true");
        } else {
          dot.removeAttribute("aria-current");
        }
      });
    };

    const scrollToIndex = (index) => {
      viewport.scrollTo({
        left: getStep() * index,
        behavior: reducedMotion.matches ? "auto" : "smooth",
      });
    };

    previous?.addEventListener("click", () =>
      scrollToIndex(Math.max(0, getIndex() - 1)),
    );
    next?.addEventListener("click", () =>
      scrollToIndex(Math.min(items.length - 1, getIndex() + 1)),
    );
    dots.forEach((dot, index) =>
      dot.addEventListener("click", () => scrollToIndex(index)),
    );

    let frame;
    viewport.addEventListener(
      "scroll",
      () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(update);
      },
      { passive: true },
    );

    if ("ResizeObserver" in window) {
      new ResizeObserver(update).observe(viewport);
    } else {
      window.addEventListener("resize", update, { passive: true });
    }

    update();
  };

  const setupCalculator = (calculator) => {
    if (calculator.dataset.iStoreFnbCalculatorReady === "true") return;

    const familySelect = calculator.querySelector("[data-fnb-family]");
    const modelSelect = calculator.querySelector("[data-fnb-model]");
    const periodSelect = calculator.querySelector("[data-fnb-period]");
    const devices = [...calculator.querySelectorAll("[data-fnb-device]")];
    const emptyMedia = calculator.querySelector("[data-fnb-empty-media]");
    const monthly = calculator.querySelector("[data-fnb-monthly]");
    const period = calculator.querySelector("[data-fnb-monthly-period]");
    const cash = calculator.querySelector("[data-fnb-cash]");
    const priceOutput = calculator.querySelector("[data-fnb-price-output]");
    const cashOutput = calculator.querySelector("[data-fnb-cash-output]");
    const emptySummary = calculator.querySelector("[data-fnb-empty-summary]");

    if (!familySelect || !modelSelect || !periodSelect) return;

    calculator.dataset.iStoreFnbCalculatorReady = "true";

    const getMatchingDevices = () =>
      devices.filter((device) => device.dataset.family === familySelect.value);

    const getSelectedDevice = () =>
      devices.find(
        (device) =>
          device.dataset.family === familySelect.value &&
          device.dataset.model === modelSelect.value,
      );

    const updateOutput = () => {
      const selected = getSelectedDevice();
      devices.forEach((device) => {
        device.hidden = device !== selected;
      });
      if (emptyMedia) emptyMedia.hidden = Boolean(selected);

      const selectedPeriod = periodSelect.value;
      const installment = selected?.dataset[`monthly${selectedPeriod}`] || "";

      if (monthly) monthly.textContent = installment;
      if (period)
        period.textContent = selectedPeriod ? `PMx${selectedPeriod}` : "";
      if (cash) cash.textContent = selected?.dataset.cash || "";
      if (priceOutput) priceOutput.hidden = !installment;
      if (cashOutput) cashOutput.hidden = !selected?.dataset.cash;
      if (emptySummary) emptySummary.hidden = Boolean(installment);
    };

    const updatePeriods = () => {
      const selected = getSelectedDevice();
      const previousValue = periodSelect.value;
      const periods = ["12", "24", "36"].filter(
        (value) => selected?.dataset[`monthly${value}`],
      );

      periodSelect.innerHTML = "";
      const placeholder = new Option(periodSelect.dataset.placeholder, "");
      periodSelect.add(placeholder);
      periods.forEach((value) => {
        periodSelect.add(new Option(`${value} months`, value));
      });
      periodSelect.disabled = !selected;
      periodSelect.value = periods.includes(previousValue) ? previousValue : "";
      updateOutput();
    };

    const updateModels = () => {
      const previousValue = modelSelect.value;
      const matching = getMatchingDevices();

      modelSelect.innerHTML = "";
      modelSelect.add(new Option(modelSelect.dataset.placeholder, ""));
      matching.forEach((device) => {
        modelSelect.add(
          new Option(device.dataset.modelLabel, device.dataset.model),
        );
      });
      modelSelect.disabled = !familySelect.value;
      modelSelect.value = matching.some(
        (device) => device.dataset.model === previousValue,
      )
        ? previousValue
        : "";
      updatePeriods();
    };

    familySelect.addEventListener("change", updateModels);
    modelSelect.addEventListener("change", updatePeriods);
    periodSelect.addEventListener("change", updateOutput);
    updateModels();
  };

  const setup = (scope = document) => {
    scope.querySelectorAll("[data-istore-fnb-rail]").forEach(setupRail);
    scope
      .querySelectorAll("[data-istore-fnb-calculator]")
      .forEach(setupCalculator);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => setup(), {
      once: true,
    });
  } else {
    setup();
  }

  document.addEventListener("shopify:section:load", (event) =>
    setup(event.target),
  );
})();
